import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import voice_recode from "../assects/img/voice_recode.png";
import red_voice_recode from "../assects/img/Red_voice_recode.png";
import gray_voice_recode from "../assects/img/gray_voice_recode.png";

export interface AvironixVoiceRecorderHandle {
  stopRecording: () => void;
}

interface Props {
  onAudioRecorded: (audioBlob: Blob) => void;
  audioLoading1: (status: boolean) => void;
  disabled?: boolean;
}

const pickMimeType = () => {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',                 // iOS Safari tends to support this
    'audio/aac',
    'audio/mpeg'
  ];
  return candidates.find(t => (window as any).MediaRecorder?.isTypeSupported?.(t)) || '';
};

const AvironixVoiceRecorder = forwardRef<AvironixVoiceRecorderHandle, Props>(
  ({ onAudioRecorded, audioLoading1, disabled }, ref) => {
    const [recording, setRecording] = useState(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunks = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);

    const startRecording = async () => {
      if (disabled || recording) return;

      const mimeType = pickMimeType();
      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1
          // sampleRate: 48000  // Safari ignores; real sample rate depends on device
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      const options: MediaRecorderOptions = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunks.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorder.onstart = () => {
        setRecording(true);
        audioLoading1(true);
      };

      mediaRecorder.onstop = () => {
        const finalType = mediaRecorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(audioChunks.current, { type: finalType });
        onAudioRecorded(blob);
        stream.getTracks().forEach(track => track.stop());
        setRecording(false);
        audioLoading1(false);
      };

      // Use a timeslice so dataavailable fires periodically (important on mobile)
      // 1000 ms is a good default
      mediaRecorder.start(1000);
    };

    const stopRecording = () => {
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state === 'recording') {
        try {
          // On iOS Safari, requestData() before stop helps flush the full buffer
          recorder.requestData();
        } catch {}
        // Give the UA a tick to emit the last dataavailable
        setTimeout(() => {
          recorder.stop();
        }, 0);
      }
    };

    useImperativeHandle(ref, () => ({ stopRecording }));

    return (
      <button
        onClick={recording ? stopRecording : startRecording}
        disabled={disabled}
        className="py-1 ps-2 rounded w-auto h-12 sm:h-[60px] overflow-hidden text-white"
      >
        {disabled ? (
          <img src={gray_voice_recode} alt="mic" className="w-auto h-full" />
        ) : recording ? (
          <img src={red_voice_recode} alt="mic" className="w-auto h-full" />
        ) : (
          <img src={voice_recode} alt="mic" className="w-auto h-full" />
        )}
      </button>
    );
  }
);

export default AvironixVoiceRecorder;
